// ThemeContext.js
import React, { createContext, useMemo, useState, useContext, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import PropTypes from "prop-types";

import getColors from "assets/theme/base/getColors";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import { normalizeColor } from "services/utils";

const ThemeContext = createContext();
export const useAppTheme = () => useContext(ThemeContext);

// 🎨 Default color constants
const DEFAULT_PRIMARY = "#00717d";

// TOP NAV
const DEFAULT_NAVBAR = "transparent";
const DEFAULT_TOP_NAVBAR_TEXT_COLOR = "#344767";

// SIDE NAV
const DEFAULT_SIDENAV = "#1c2536";
const DEFAULT_NAVBAR_TEXT = "#ffffff"; // 🆕

const DEFAULT_BACKGROUND = "#f0f2f5";
const DEFAULT_NAVBAR_TEXT_SIZE = "2rem"; // 🆕

// const DEFAULT_PRIMARY = "#00717d";
// const DEFAULT_NAVBAR = "transparent";
// const DEFAULT_SIDENAV = "transparent";
// const DEFAULT_BACKGROUND = "#f4f6f8";
// const DEFAULT_NAVBAR_TEXT = "#7b809a"; // 🆕 Default navbar text color
// const DEFAULT_TOP_NAVBAR_TEXT_COLOR = "#7b809a";

const AppThemeProvider = ({
  children,
  externalPrimaryColor,
  externalNavBarColor,
  externalNavBarTextColor, // 🆕
  externalNavBarTextSize, // 🆕
  externalSideNavColor,
  externalBackgroundColor,
  externalTopNavBarTextColor,
}) => {
  // 🎨 Dynamic color states
  const [primaryColor, setPrimaryColor] = useState(externalPrimaryColor || DEFAULT_PRIMARY);
  const [navBarColor, setNavBarColor] = useState(externalNavBarColor || DEFAULT_NAVBAR);
  const [sideNavColor, setSideNavColor] = useState(externalSideNavColor || DEFAULT_SIDENAV);
  const [backgroundColor, setBackgroundColor] = useState(
    externalBackgroundColor || DEFAULT_BACKGROUND
  );
  const [navBarTextColor, setNavBarTextColor] = useState(
    externalNavBarTextColor || DEFAULT_NAVBAR_TEXT
  ); // 🆕
  const [navBarTextSize, setNavBarTextSize] = useState(
    externalNavBarTextSize || DEFAULT_NAVBAR_TEXT_SIZE
  ); // 🆕
  const [darkMode, setDarkMode] = useState(false);
  const [topNavBarTextColor, setTopNavBarTextColor] = useState(
    externalTopNavBarTextColor || DEFAULT_TOP_NAVBAR_TEXT_COLOR
  );

  // 🔁 Sync external props
  useEffect(() => {
    if (externalPrimaryColor) setPrimaryColor(externalPrimaryColor);
    if (externalNavBarColor) setNavBarColor(externalNavBarColor);
    if (externalSideNavColor) setSideNavColor(externalSideNavColor);
    if (externalBackgroundColor) setBackgroundColor(externalBackgroundColor);
    if (externalNavBarTextColor) setNavBarTextColor(externalNavBarTextColor); // 🆕
    if (externalNavBarTextSize) setNavBarTextSize(externalNavBarTextSize); // 🆕
    if (externalTopNavBarTextColor) setTopNavBarTextColor(externalTopNavBarTextColor);
  }, [
    externalPrimaryColor,
    externalNavBarColor,
    externalSideNavColor,
    externalBackgroundColor,
    externalNavBarTextColor,
    externalNavBarTextSize,
    externalTopNavBarTextColor,
  ]);

  // 🌈 Derived palette
  const colors = useMemo(() => getColors(primaryColor), [primaryColor]);

  // 🌓 Base theme (RTL or LTR)
  const baseTheme = darkMode ? themeDarkRTL : themeRTL;

  // 🧩 Merge overrides
  const dynamicTheme = useMemo(() => {
    const overrides = {
      palette: {
        primary: {
          main: normalizeColor(colors.primary?.main || primaryColor),
          contrastText: "#fff",
        },
        gradients: {
          ...baseTheme.palette.gradients,
          primary: {
            main: normalizeColor(colors.primary?.main || primaryColor),
            state: normalizeColor(colors.primary?.main || primaryColor),
          },
        },
        badgeColors: {
          ...baseTheme.palette.badgeColors,
          primary: {
            background: normalizeColor(colors.primary?.main || primaryColor),
            text: normalizeColor(colors.primary?.main || primaryColor),
          },
        },
        coloredShadows: {
          ...baseTheme.palette.coloredShadows,
          primary: normalizeColor(colors.primary?.main || primaryColor),
        },
        navBar: {
          main: normalizeColor(navBarColor),
          contrastText: normalizeColor(topNavBarTextColor), // 🆕
        },
        sideNav: {
          main: normalizeColor(sideNavColor),
          contrastText: normalizeColor(navBarTextColor),
        },
        background: {
          default: normalizeColor(backgroundColor),
          main: normalizeColor(backgroundColor),
        },
      },
      typography: {
        // 🆕 Apply navbar text size globally under a custom variant
        navBarText: {
          fontSize: navBarTextSize,
          color: normalizeColor(navBarTextColor),
          fontWeight: 500,
        },
      },
    };

    return createTheme(deepmerge(baseTheme, overrides));
  }, [
    colors,
    baseTheme,
    primaryColor,
    navBarColor,
    sideNavColor,
    backgroundColor,
    navBarTextColor,
    navBarTextSize,
    topNavBarTextColor,
    DEFAULT_PRIMARY,
    DEFAULT_NAVBAR,
    DEFAULT_SIDENAV,
    DEFAULT_BACKGROUND,
    DEFAULT_NAVBAR_TEXT,
    DEFAULT_TOP_NAVBAR_TEXT_COLOR,
  ]);

  return (
    <ThemeContext.Provider
      value={{
        primaryColor,
        setPrimaryColor,
        navBarColor,
        setNavBarColor,
        navBarTextColor, // 🆕
        setNavBarTextColor, // 🆕
        navBarTextSize, // 🆕
        setNavBarTextSize, // 🆕
        sideNavColor,
        setSideNavColor,
        topNavBarTextColor,
        setTopNavBarTextColor,
        backgroundColor,
        setBackgroundColor,
        darkMode,
        setDarkMode,
        DEFAULT_PRIMARY,
        DEFAULT_NAVBAR,
        DEFAULT_SIDENAV,
        DEFAULT_BACKGROUND,
        DEFAULT_NAVBAR_TEXT,
        DEFAULT_TOP_NAVBAR_TEXT_COLOR,
      }}
    >
      <ThemeProvider theme={dynamicTheme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

AppThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  externalPrimaryColor: PropTypes.string,
  externalNavBarColor: PropTypes.string,
  externalSideNavColor: PropTypes.string,
  externalBackgroundColor: PropTypes.string, // 🆕
  externalNavBarTextColor: PropTypes.string,
  externalNavBarTextSize: PropTypes.number,
  externalTopNavBarTextColor: PropTypes.string,
};

export default AppThemeProvider;
